import { useEffect, useState } from 'react';
import { Box, Grid, LinearProgress, Paper, Snackbar, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';

//import { ParceirosService } from '../../shared/services/api/parceiro/ParceirosService';
import { VTextField, VForm, useVForm, IVFormErrors } from '../../shared/forms';
import { FerramentasDeDetalhe } from '../../shared/components';
import { LayoutBaseDePagina } from '../../shared/layouts';
import { ParceirosServiceAPI } from '../../shared/services/api/parceiro/ParceirosService';
import { useAuth } from '../../shared/contexts';
import ConfirmDeleteDialog from './components/ConfirmDeleteDialog';
import { IFormPartnerData } from '../../shared/types/types';


const formValidationSchema: yup.Schema<IFormPartnerData> = yup.object().shape({
  nome_customer: yup
    .string()
    .required('O campo nome do cliente é obrigatório.')
    .min(3, 'O nome deve ter pelo menos 3 caracteres.'),
  cpf_cnpj: yup
    .string()
    .required('O campo CPF/CNPJ é obrigatório.')
    .min(11, 'O CPF/CNPJ deve ter pelo menos 11 caracteres.'),
  phone: yup
    .string()
    .required('O campo telefone é obrigatório.')
    .min(10, 'O telefone deve ter pelo menos 10 caracteres.'),  // Ajuste conforme a necessidade
  ip_cloud: yup
    .string()
    .required('O campo IP da nuvem é obrigatório.'),
  user_cloud: yup
    .string()
    .required('O campo usuário da nuvem é obrigatório.')
    .min(3, 'O nome de usuário da nuvem deve ter pelo menos 3 caracteres.'),
  pass_cloud: yup
    .string()
    .required('O campo senha da nuvem é obrigatório.')
    .min(3, 'A senha da nuvem deve ter pelo menos 3 caracteres.')
});



export const DetalheDeParceiros: React.FC = () => {
  const { formRef, save, saveAndClose, isSaveAndClose } = useVForm();
  const { id = 'nova' } = useParams<'id'>();
  const navigate = useNavigate();
  const { getUser } = useAuth();


  const [isLoading, setIsLoading] = useState(false);
  const [nome, setNome] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');


  useEffect(() => {
    if (id !== 'nova') {
      setIsLoading(true);

      ParceirosServiceAPI.getById(getUser(), id)
        .then((result) => {
          setIsLoading(false);

          if (result instanceof Error) {
            setSnackbarMessage(result.message);
            navigate('/parceiros');
          } else {
            setNome(result.data.nome_customer);

            // Setar os dados no formulário incluindo apenas os IDs das roles
            formRef.current?.setData({ ...result.data });
          }
        });
    } else {
      formRef.current?.setData({
        email: '',
        nomeCompleto: '',
        roles: undefined,
      });
    }
  }, [id, getUser, navigate]);



  const handleSave = (dados: IFormPartnerData) => {
    console.log(dados)
    formValidationSchema.
      validate(dados, { abortEarly: false })
      .then((dadosValidados) => {
        setIsLoading(true);

        if (id === 'nova') {
          ParceirosServiceAPI
            .create(getUser(), dadosValidados)
            .then((result) => {
              console.log(result.data);
              setIsLoading(false);

              if (result instanceof Error) {
                setSnackbarMessage(result.message);
              } else {
                if (isSaveAndClose()) {
                  navigate('/parceiros');
                } else {
                  navigate(`/parceiros/detalhe/${result.data.id}`);
                }
              }
            });
        } else {

          ParceirosServiceAPI
            .updateById(getUser(), id, { ...dadosValidados })
            .then((result) => {
              setIsLoading(false);

              if (result instanceof Error) {
                setSnackbarMessage(result.message);
              } else {
                if (isSaveAndClose()) {
                  navigate('/parceiros');
                }
              }
            });
        }
      })
      .catch((errors: yup.ValidationError) => {
        const validationErrors: IVFormErrors = {};

        errors.inner.forEach(error => {
          if (!error.path) return;

          validationErrors[error.path] = error.message;
        });

        formRef.current?.setErrors(validationErrors);
      });
  };

  const handleDelete = () => {
    setOpenDialog(true); // Abre o diálogo de confirmação
  };

  const handleConfirmDelete = () => {
    //if (confirm('Realmente deseja apagar?')) {
    ParceirosServiceAPI.deleteById(getUser(), id)
      .then(result => {
        if (result instanceof Error) {
          setSnackbarMessage(result.message);
        } else {
          setSnackbarMessage('Registro apagado com sucesso!');
          navigate('/parceiros');
        }
      });
    //}
  };

  // Função para `onPointerEnterCapture`
  const handlePointerEnterCapture = (event: React.PointerEvent) => {
    console.log("Pointer entered:", event);
  };

  // Função para `onPointerLeaveCapture`
  const handlePointerLeaveCapture = (event: React.PointerEvent) => {
    console.log("Pointer left:", event);
  };

  // Propriedade para `placeholder`
  const placeholderText = "Digite aqui..."; // Substitua por algo útil ou `undefined`


  return (
    <LayoutBaseDePagina
      titulo={id === 'nova' ? 'Nova pessoa' : nome}
      barraDeFerramentas={
        <FerramentasDeDetalhe
          textoBotaoNovo='Nova'
          mostrarBotaoSalvarEFechar
          mostrarBotaoNovo={id !== 'nova'}
          mostrarBotaoApagar={id !== 'nova'}
          mostrarBotaoSalvarCarregando={isLoading}

          aoClicarEmSalvar={save}
          aoClicarEmSalvarEFechar={saveAndClose}
          aoClicarEmVoltar={() => navigate('/parceiros')}
          aoClicarEmApagar={handleDelete}
          aoClicarEmNovo={() => navigate('/parceiros/detalhe/nova')}
        />
      }
    >
      <ConfirmDeleteDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        onConfirm={handleConfirmDelete}
      />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
      <VForm ref={formRef} onSubmit={handleSave} placeholder={placeholderText} onPointerEnterCapture={handlePointerEnterCapture} onPointerLeaveCapture={handlePointerLeaveCapture}>
        <Box margin={1} display="flex" flexDirection="column" component={Paper} variant="outlined">

          <Grid container direction="column" padding={2} spacing={2} width="100%">

            {isLoading && (
              <Grid item>
                <LinearProgress variant='indeterminate' />
              </Grid>
            )}

            <Grid item>
              <Typography variant='h6'>Conta</Typography>
            </Grid>

            {/* Nome completo ocupando toda a linha */}
            <Grid item xs={12}>
              <VTextField
                fullWidth
                name='nome_customer'
                disabled={isLoading}
                label='Nome completo'
                onChange={e => setNome(e.target.value)}
              />
            </Grid>

            {/* CPF/CNPJ e Telefone na mesma linha */}
            <Grid container item spacing={2}>
              <Grid item xs={6}>
                <VTextField
                  fullWidth
                  name='cpf_cnpj'
                  label='CPF/CNPJ'
                  disabled={isLoading}
                />
              </Grid>
              <Grid item xs={6}>
                <VTextField
                  fullWidth
                  name='phone'
                  label='Telefone'
                  disabled={isLoading}
                />
              </Grid>
            </Grid>

            {/* Outros campos em linhas separadas */}
            <Grid container item spacing={2}>
              <Grid item xs={6}>
                <VTextField
                  fullWidth
                  name='currency'
                  label='Valor do Bilhete'
                  disabled={isLoading}
                />
              </Grid>
              <Grid item xs={6}>
                <VTextField
                  fullWidth
                  name='user_cloud'
                  label='Usuário da Nuvem'
                  disabled={isLoading}
                />
              </Grid>
            </Grid>

            <Grid container item spacing={2}>
              <Grid item xs={6}>
                <VTextField
                  fullWidth
                  name='ip_cloud'
                  label='IP da Nuvem'
                  disabled={isLoading}
                />
              </Grid>
              <Grid item xs={6}>
                <VTextField
                  fullWidth
                  name='pass_cloud'
                  label='Senha da Nuvem'
                  disabled={isLoading}
                />
              </Grid>
            </Grid>

          </Grid>

        </Box>
      </VForm>
    </LayoutBaseDePagina>
  );
};
